import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { propertyApi } from '../services/BaseApiService.jsx';

/**
 * Contexto para configuração de relatório (report_config).
 * Carrega o payload a partir do backend (/api/v1/report-config)
 * e expõe helpers para visibilidade, labels e estilos.
 */
const ReportConfigContext = createContext(null);

export function ReportConfigProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadConfig() {
      setLoading(true);
      setError(null);
      try {
        const resp = await propertyApi.get('/report-config');
        if (!isMounted) return;
        const data = resp?.data || null;
        if (data && typeof data === 'object') {
          setConfig(data);
        } else {
          setConfig(null);
          setError('Configuração de relatório inválida (payload não é um objeto).');
        }
      } catch (e) {
        console.error('[ReportConfig] Erro ao carregar configuração de relatório:', e);
        if (!isMounted) return;
        setError('Erro ao carregar configuração de relatório.');
        setConfig(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const helpers = useMemo(() => {
    const visibility = config?.visibility || {};
    const labels = config?.labels || {};
    const styles = config?.styles || {};
    const roles = styles?.roles || {};

    const fontSizeMap = {
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
    };

    const isDomainEnabled = (domainKey) => {
      if (!domainKey) return true;
      const domainCfg = visibility[domainKey];
      if (!domainCfg || typeof domainCfg !== 'object') return true;
      if (typeof domainCfg.enabled === 'boolean') return domainCfg.enabled;
      return true;
    };

    const isSectionEnabled = (domainKey, sectionKey) => {
      if (!domainKey || !sectionKey) return true;
      if (!isDomainEnabled(domainKey)) return false;
      const domainCfg = visibility[domainKey];
      const sections = domainCfg?.sections || {};
      const sectionCfg = sections[sectionKey];
      if (!sectionCfg || typeof sectionCfg !== 'object') return true;
      if (typeof sectionCfg.enabled === 'boolean') return sectionCfg.enabled;
      return true;
    };

    const isFieldEnabled = (domainKey, sectionKey, fieldKey) => {
      if (!fieldKey) return true;
      if (domainKey && sectionKey && !isSectionEnabled(domainKey, sectionKey)) {
        return false;
      }
      if (!domainKey || !sectionKey) return true;
      const domainCfg = visibility[domainKey];
      const sectionCfg = domainCfg?.sections?.[sectionKey];
      const fields = sectionCfg?.fields || {};
      const fieldCfg = fields[fieldKey];
      if (!fieldCfg || typeof fieldCfg !== 'object') return true;
      if (typeof fieldCfg.enabled === 'boolean') return fieldCfg.enabled;
      return true;
    };

    const getFieldLabel = (domainKey, fieldKey, defaultLabel) => {
      if (!fieldKey) return defaultLabel || '';
      const domainLabels = (domainKey && labels[domainKey]) || {};
      const globalLabels = labels.global || {};
      return domainLabels[fieldKey] || globalLabels[fieldKey] || defaultLabel || fieldKey;
    };

    const getStyleForRole = (roleName, fallback = {}) => {
      if (!roleName) return fallback || {};
      const roleCfg = roles[roleName];
      if (!roleCfg || typeof roleCfg !== 'object') return fallback || {};

      const resolved = { ...roleCfg };

      if (resolved.fontSize && fontSizeMap[resolved.fontSize]) {
        resolved.fontSize = fontSizeMap[resolved.fontSize];
      }

      return { ...fallback, ...resolved };
    };

    const getFieldStyle = (domainKey, fieldKey, fallback = {}) => {
      if (!domainKey || !fieldKey) return fallback || {};
      const domainStyles = styles[domainKey] || {};
      const fieldStyle = domainStyles[fieldKey] || {};

      // Converter valores numéricos para unidades CSS quando necessário
      const resolved = { ...fieldStyle };

      if (resolved.fontSize && typeof resolved.fontSize === 'number') {
        resolved.fontSize = `${resolved.fontSize}px`;
      }

      if (resolved.padding && typeof resolved.padding === 'number') {
        resolved.padding = `${resolved.padding}px`;
      }

      if (resolved.margin && typeof resolved.margin === 'number') {
        resolved.margin = `${resolved.margin}px`;
      }

      if (resolved.borderRadius && typeof resolved.borderRadius === 'number') {
        resolved.borderRadius = `${resolved.borderRadius}px`;
      }

      if (resolved.letterSpacing && typeof resolved.letterSpacing === 'number') {
        resolved.letterSpacing = `${resolved.letterSpacing}px`;
      }

      return { ...fallback, ...resolved };
    };

    return {
      isDomainEnabled,
      isSectionEnabled,
      isFieldEnabled,
      getFieldLabel,
      getStyleForRole,
      getFieldStyle,
    };
  }, [config]);

  const value = useMemo(
    () => ({
      config,
      loading,
      error,
      ...helpers,
    }),
    [config, loading, error, helpers],
  );

  return <ReportConfigContext.Provider value={value}>{children}</ReportConfigContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReportConfig() {
  const ctx = useContext(ReportConfigContext);
  if (!ctx) {
    throw new Error('useReportConfig deve ser usado dentro de um ReportConfigProvider');
  }
  return ctx;
}

export default ReportConfigContext;
